import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";

import { useListAccountsQuery } from "features/trading/accounts";
import {
  BucketNumberEnum,
  useListBucketsQuery,
} from "features/trading/buckets";
import {
  formatAccountLabel,
  formatMemberCounts,
} from "./constants";
import { BucketEditModal } from "./BucketEditModal";
import { InlineLoading } from "./InlineLoading";

const ACCOUNTS_PAGE_SIZE = 100;

interface ClientBucket3SelectorProps {
  clientId: number | null | undefined;
  // Fired whenever the Bucket 3 the report should use changes (including
  // the initial auto-selection and a bucket created from the modal).
  // Null = the selected account has no Bucket 3 / nothing selected.
  onBucket3Change?: (bucketId: number | null) => void;
}

/**
 * Bucket 3 picker for the report settings: the client is already chosen on
 * the page, and a client may have several accounts — several Bucket 3s, or
 * none. This selects WHICH account's Bucket 3 the report will use, shows
 * what's inside it, and opens the shared BucketEditModal to edit/create it.
 */
export const ClientBucket3Selector = ({
  clientId,
  onBucket3Change,
}: ClientBucket3SelectorProps) => {
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(
    null,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: accountsData, isFetching: isFetchingAccounts } =
    useListAccountsQuery(
      {
        filters: { client: clientId },
        pagination: { page: 0, pageSize: ACCOUNTS_PAGE_SIZE },
      },
      { skip: !clientId },
    );
  const { data: bucketsData, isFetching: isFetchingBuckets } =
    useListBucketsQuery(
      { account__client: clientId as number },
      { skip: !clientId },
    );
  const isLoading =
    (isFetchingAccounts && !accountsData) ||
    (isFetchingBuckets && !bucketsData);

  const accounts = useMemo(
    () => accountsData?.results ?? [],
    [accountsData],
  );
  const bucket3ByAccount = useMemo(
    () =>
      new Map(
        (bucketsData?.results ?? [])
          .filter((bucket) => bucket.number === BucketNumberEnum.BUCKET_3)
          .map((bucket) => [bucket.account, bucket]),
      ),
    [bucketsData],
  );

  // Client switched — the previous selection belongs to another client.
  useEffect(() => {
    setSelectedAccountId(null);
  }, [clientId]);

  // Default selection: the first account that already has a Bucket 3
  // (a lone account is selected even without one).
  useEffect(() => {
    if (selectedAccountId !== null || !accountsData || !bucketsData) return;
    const withBucket = accounts.find((a) => bucket3ByAccount.has(a.id));
    const fallback = accounts.length === 1 ? accounts[0] : undefined;
    const target = withBucket ?? fallback;
    if (target) setSelectedAccountId(target.id);
  }, [
    accounts,
    accountsData,
    bucketsData,
    bucket3ByAccount,
    selectedAccountId,
  ]);

  // The report needs the bucket's id, not the account's: the backend
  // derives the account from the exact Bucket 3 it's given.
  const selectedBucket = selectedAccountId
    ? bucket3ByAccount.get(selectedAccountId)
    : undefined;
  const selectedBucketId = selectedBucket?.id ?? null;
  useEffect(() => {
    onBucket3Change?.(selectedBucketId);
  }, [selectedBucketId, onBucket3Change]);

  if (!clientId) {
    return (
      <Typography variant="body2" color="text.disabled">
        Select a client
      </Typography>
    );
  }

  if (isLoading) {
    return <InlineLoading />;
  }

  if (accounts.length === 0) {
    return (
      <Typography variant="body2" color="text.disabled">
        The client has no accounts
      </Typography>
    );
  }

  const selectedAccount = accounts.find((a) => a.id === selectedAccountId);

  return (
    <>
      <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
        <Select
          size="small"
          displayEmpty
          value={selectedAccountId ?? ""}
          onChange={(event) =>
            setSelectedAccountId(
              event.target.value === "" ? null : Number(event.target.value),
            )
          }
          renderValue={(value) => {
            const account = accounts.find((a) => a.id === value);
            return account ? (
              formatAccountLabel(account)
            ) : (
              <Typography component="span" color="text.disabled">
                Select account
              </Typography>
            );
          }}
        >
          {accounts.map((account) => {
            const bucket = bucket3ByAccount.get(account.id);
            return (
              <MenuItem key={account.id} value={account.id}>
                <Box display="flex" alignItems="center" gap={1} width="100%">
                  <Typography component="span" flexGrow={1}>
                    {formatAccountLabel(account)}
                  </Typography>
                  {bucket ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${bucket.assets.length} securities`}
                    />
                  ) : (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.disabled"
                    >
                      no Bucket 3
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            );
          })}
        </Select>
        {selectedAccount &&
          (selectedBucket ? (
            <>
              <Chip
                size="small"
                variant="outlined"
                label={formatMemberCounts(selectedBucket)}
                onClick={() => setIsModalOpen(true)}
              />
              <Tooltip title="View Bucket 3">
                <IconButton size="small" onClick={() => setIsModalOpen(true)}>
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setIsModalOpen(true)}
            >
              Add Bucket 3
            </Button>
          ))}
      </Box>
      <BucketEditModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        accountId={selectedAccountId}
        accountLabel={selectedAccount ? formatAccountLabel(selectedAccount) : ""}
        number={BucketNumberEnum.BUCKET_3}
        bucket={selectedBucket}
      />
    </>
  );
};
